/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * The 12-key pad.
 *
 * Serves two jobs that look the same and are not: sending DTMF tones into a
 * live call, and composing a number to dial. The console previously had only
 * the first, so an agent who wanted to key in a number had no pad at all and
 * had to type into a text field.
 *
 * `onKey` receives the character. The caller decides whether that means "play
 * a tone down the line" or "append to the number being dialled"; this
 * component deliberately knows about neither.
 */

import React from "react";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import colors from "styles/tokens/colors";

const cc = colors.callCenter;

export const DTMF_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];

// The letters under each digit. Not decoration: agents read numbers off
// vanity spellings ("call 1-800-FLOWERS") and off written notes.
const KEY_LETTERS = {
  2: "ABC",
  3: "DEF",
  4: "GHI",
  5: "JKL",
  6: "MNO",
  7: "PQRS",
  8: "TUV",
  9: "WXYZ",
};

export default function Dialpad({ onKey, disabled, showLetters = true, size = "medium" }) {
  const compact = size === "small";

  return (
    <MDBox
      display="grid"
      gridTemplateColumns="repeat(3, 1fr)"
      gap={compact ? 0.5 : 1}
      role="group"
      aria-label="Dialpad"
    >
      {DTMF_KEYS.map((key) => (
        <MDButton
          key={key}
          variant="outlined"
          color="dark"
          size={compact ? "small" : "medium"}
          disabled={disabled}
          onClick={() => onKey(key)}
          sx={{
            minWidth: 0,
            py: compact ? 0.5 : 1,
            lineHeight: 1.1,
            borderColor: cc.cardBorder,
            flexDirection: "column",
          }}
        >
          <MDBox component="span" sx={{ fontSize: compact ? "0.9rem" : "1.05rem" }}>
            {key}
          </MDBox>
          {showLetters && !compact && KEY_LETTERS[key] ? (
            <MDBox
              component="span"
              sx={{ fontSize: "0.55rem", letterSpacing: "0.08em", color: cc.muted }}
            >
              {KEY_LETTERS[key]}
            </MDBox>
          ) : null}
        </MDButton>
      ))}
    </MDBox>
  );
}
